'use client';

import { useState } from 'react';
import { PlayerType, GameSettingsType } from '../types';
import SettingsPanel from './SettingsPanel';

type GameControlsProps = {
  currentPlayer: PlayerType;
  gameOver: boolean;
  moveCount: number;
  winner: PlayerType | null;
  isAITurn: boolean;
  aiIsThinking: boolean;
  settings: GameSettingsType;
  onReset: () => void;
  onUpdateSettings: (settings: Partial<GameSettingsType>) => void;
};

export const GameControls: React.FC<GameControlsProps> = ({
  currentPlayer,
  gameOver,
  moveCount,
  winner,
  isAITurn,
  aiIsThinking,
  settings,
  onReset,
  onUpdateSettings,
}) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Get player labels based on game mode
  const getPlayerLabel = (player: PlayerType) => {
    if (settings.gameMode === 'human-vs-human') {
      return player === 'BLACK' ? 'Black' : 'White';
    } else {
      // Human vs AI mode
      const isHuman = player === settings.humanPlayerColor;
      return isHuman 
        ? `You (${player === 'BLACK' ? 'Black' : 'White'})` 
        : `AI (${player === 'BLACK' ? 'Black' : 'White'})`;
    }
  };

  // Get difficulty badge color
  const getDifficultyColor = () => {
    switch (settings.aiDifficulty) {
      case 'easy':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'hard':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="w-full max-w-[600px] mx-auto p-4 rounded-lg bg-white/80 shadow-sm mt-4">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {!gameOver ? (
            <>
              <div className="text-lg font-medium">Current turn:</div>
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full ${
                  currentPlayer === 'BLACK' ? 'bg-gray-900' : 'bg-gray-100 border border-gray-300'
                }`}></div>
                <span className="font-semibold">
                  {getPlayerLabel(currentPlayer)}
                </span>
                {aiIsThinking && (
                  <span className="inline-flex items-center ml-2 text-sm text-gray-500">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Thinking...
                  </span>
                )}
              </div>
            </>
          ) : winner ? (
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full ${
                winner === 'BLACK' ? 'bg-gray-900' : 'bg-gray-100 border border-gray-300'
              }`}></div>
              <span className="font-semibold text-lg text-indigo-700">
                {getPlayerLabel(winner)} won!
              </span>
            </div>
          ) : (
            <div className="font-semibold text-lg text-indigo-700">Game ended in a draw!</div>
          )}
          
          <div className="text-sm text-gray-500 ml-2">
            Moves: {moveCount}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {settings.gameMode === 'human-vs-ai' && (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getDifficultyColor()}`}>
              AI: {settings.aiDifficulty.charAt(0).toUpperCase() + settings.aiDifficulty.slice(1)}
            </span>
          )}
          
          <button
            onClick={() => setIsSettingsOpen(true)}
            className="btn btn-outline btn-sm border-indigo-300 hover:bg-indigo-100 hover:border-indigo-400 text-indigo-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Settings
          </button>
          
          <button
            onClick={onReset}
            className="btn bg-indigo-500 hover:bg-indigo-600 text-white border-none btn-sm"
          >
            {gameOver ? 'New Game' : 'Restart'}
          </button>
        </div>
      </div>
      
      {!gameOver && (
        <div className="mt-4 text-sm text-gray-600">
          Get five stones in a row (horizontally, vertically, or diagonally) to win!
        </div>
      )}
      
      <SettingsPanel
        settings={settings}
        onSettingsChange={onUpdateSettings}
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </div>
  );
};

export default GameControls;